import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from 'hooks/useToaster';
import { getCustomers } from 'lib/api/getCustomers';
import { getLocations } from 'lib/api/getLocations';
import { CompanyCoreInfo, TMSCustomer, TMSLocation } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  GenericCompanySearchableFields,
  injectSelectedObject,
} from 'utils/loadInfoAndBuilding';

import { PLS_BILL_TO_SEARCH_API_ENABLED } from '../constants';
import {
  buildPlsBillTosFromLocations,
  mapPlsBillTosToSelectOptions,
} from '../utils';

export function usePlsData(
  tmsID: number | undefined,
  customerExternalTmsId: string | undefined,
  selectedCustomer?: TMSCustomer
) {
  const { toast } = useToast();

  const [customers, setCustomers] = useState<Maybe<TMSCustomer[]>>(null);
  const [locations, setLocations] = useState<Maybe<TMSLocation[]>>(null);
  const [locationCatalog, setLocationCatalog] =
    useState<Maybe<TMSLocation[]>>(null);
  const [locationCatalogCustomerId, setLocationCatalogCustomerId] =
    useState<Undef<string>>();
  const [billTos, setBillTos] = useState<Maybe<CompanyCoreInfo[]>>(null);

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);

  const currentLocationRequestCustomerIdRef = useRef<Undef<string>>(undefined);
  const normalizedCustomerExternalTmsId = (customerExternalTmsId ?? '').trim();

  const refreshCustomers = useCallback(async () => {
    if (!tmsID) {
      return;
    }

    setIsLoadingCustomers(true);
    try {
      const result = await getCustomers(tmsID);
      if (result.isOk()) {
        const customerList = result.value.customerList ?? [];
        setCustomers(
          selectedCustomer?.externalTMSID
            ? injectSelectedObject(selectedCustomer, customerList)
            : customerList
        );
      } else {
        toast({
          description: result.error.message,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoadingCustomers(false);
    }
  }, [tmsID, toast, selectedCustomer]);

  const refreshLocations = useCallback(async () => {
    const requestCustomerExternalTmsId = normalizedCustomerExternalTmsId;
    if (!tmsID || !requestCustomerExternalTmsId) {
      currentLocationRequestCustomerIdRef.current = undefined;
      setLocations([]);
      setLocationCatalog([]);
      setLocationCatalogCustomerId(undefined);
      setBillTos([]);
      return;
    }

    currentLocationRequestCustomerIdRef.current = requestCustomerExternalTmsId;
    setIsLoadingLocations(true);

    try {
      const result = await getLocations(
        tmsID,
        true,
        false,
        requestCustomerExternalTmsId
      );

      if (
        currentLocationRequestCustomerIdRef.current !==
        requestCustomerExternalTmsId
      ) {
        return;
      }

      if (result.isOk()) {
        const customerLocations = result.value.locationList ?? [];
        setLocations(customerLocations);
        setLocationCatalog(customerLocations);
        setLocationCatalogCustomerId(requestCustomerExternalTmsId);
        setBillTos(buildPlsBillTosFromLocations(customerLocations));
      } else {
        toast({
          description: result.error.message,
          variant: 'destructive',
        });
        setLocations([]);
        setLocationCatalog([]);
        setLocationCatalogCustomerId(undefined);
        setBillTos([]);
      }
    } finally {
      if (
        currentLocationRequestCustomerIdRef.current ===
        requestCustomerExternalTmsId
      ) {
        setIsLoadingLocations(false);
      }
    }
  }, [tmsID, normalizedCustomerExternalTmsId, toast]);

  const refreshBillTos = useCallback(async () => {
    setBillTos(buildPlsBillTosFromLocations(locationCatalog));
  }, [locationCatalog]);

  const handleBillToSearch = useCallback(
    async (field: GenericCompanySearchableFields, value: string) => {
      const searchTerm = (value ?? '').trim().toLowerCase();
      const filteredBillTos = (billTos ?? []).filter((billTo) =>
        String(billTo[field] ?? '')
          .toLowerCase()
          .includes(searchTerm)
      );

      return mapPlsBillTosToSelectOptions(filteredBillTos);
    },
    [billTos]
  );

  useEffect(() => {
    if (!tmsID) {
      return;
    }
    void refreshCustomers();
  }, [tmsID, refreshCustomers]);

  useEffect(() => {
    if (!selectedCustomer?.externalTMSID) {
      return;
    }

    setCustomers((currentCustomers) =>
      injectSelectedObject(selectedCustomer, currentCustomers ?? [])
    );
  }, [selectedCustomer]);

  useEffect(() => {
    if (!normalizedCustomerExternalTmsId) {
      currentLocationRequestCustomerIdRef.current = undefined;
      setLocations([]);
      setLocationCatalog([]);
      setLocationCatalogCustomerId(undefined);
      setBillTos([]);
      return;
    }

    setLocations([]);
    setLocationCatalog([]);
    setLocationCatalogCustomerId(undefined);
    setBillTos([]);
    void refreshLocations();

    return () => {
      currentLocationRequestCustomerIdRef.current = undefined;
    };
  }, [normalizedCustomerExternalTmsId, refreshLocations]);

  return {
    customers,
    setCustomers,
    locations,
    setLocations,
    locationCatalog,
    locationCatalogCustomerId,
    billTos,
    setBillTos,
    isLoading: {
      customers: isLoadingCustomers,
      locations: isLoadingLocations,
      // Bill-tos are derived from the customer location catalog fetch.
      billTos: isLoadingLocations,
    },
    setIsLoadingCustomers,
    handleBillToSearch,
    refreshCustomers,
    refreshLocations,
    refreshBillTos,
    isBillToSearchApiEnabled: PLS_BILL_TO_SEARCH_API_ENABLED,
  };
}
